import { useState, useCallback, useRef, useEffect, useContext, useMemo } from "react";
import { Form, Container, Button, Row, Col, Card } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNImage } from '../../globals';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import { es, enUS, de } from 'date-fns/locale';
import { HotelContext } from "../../context/hotel";
import { useParams, useNavigate } from "react-router";
import { faArrowLeft, faAngleLeft, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { T } from "react-translator-component";
import { getLocale } from "../../functions";
import { serviceTypeIcon } from '../../globals';
import { i18nValue } from '../../functions';
import { Chat as ChatIcon, Avatar, Empty } from '../../components/Icons';
import usePerson from "../../hooks/usePerson";
import { useLanguage } from "../../hooks/useLanguage";


const PublicChat = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const refComment = useRef(null);
    const refItemsContainer = useRef(null);
    const [items, setItems] = useState([]);
    const [message, setMessage] = useState(null);
    const { data: hotel } = useContext(HotelContext);
    const [sending, setSending] = useState(false);
    const { language: languageApp } = useLanguage();
    const { addPublicRequestComment, getRequest: getPublicRequest } = usePerson();
    const [request, setRequest] = useState(null);
    let prevMessageUser = null;

    // Obtener el idioma del usuario
    const language = useMemo(() => {
        return languageApp ?? "en-US";
    }, [languageApp]);

    // Cargar el request público cuando se monta el componente
    useEffect(() => {
        if (id) {
            getPublicRequest(id).then(r => {
                setRequest(r);
                setItems(r.evolutions || []);
            }).catch(error => {
                console.error('Error al cargar request público:', error);
            });
        }
    }, [id]);

    // Actualizar items cuando cambia el request
    useEffect(() => {
        if (request && request.id === id) {
            setItems(request.evolutions || []);
        }
    }, [request]);

    const _addComment = (comment) => {
        if (!comment || !comment.trim()) return;
        
        setSending(true);
        addPublicRequestComment(id, comment).then(r => {
            refComment.current.style.height = "auto";
            refComment.current.value = '';
            setMessage(false);
            // Recargar el request para obtener los comentarios actualizados
            getPublicRequest(id).then(updatedRequest => {
                setRequest(updatedRequest);
                setItems(updatedRequest.evolutions || []);
                setSending(false);
            });
        }).catch(error => {
            console.error('Error al agregar comentario:', error);
            setSending(false);
        });
    };

    const scrollToBottom = () => {
        if (refItemsContainer?.current) {
            refItemsContainer.current.scrollTop = refItemsContainer.current.scrollHeight;
        }
    };

    const getLastMessageIndexByUser = (items) => {
        const lastMessageIndex = {};
        
        if (items) {
            items.forEach((item, idx) => {
                lastMessageIndex[item.userId] = idx;
            });
        }
        return lastMessageIndex;
    };

    const lastMessageIndexByUser = getLastMessageIndexByUser(items);

    useEffect(() => {
        scrollToBottom();
    }, [items]);

    const handleInputHeight = () => {
        refComment.current.style.height = "1px";
        const scrollHeight = refComment.current.scrollHeight;
        refComment.current.style.height = scrollHeight + "px";
        setMessage(refComment.current.value);
    };

    if (!request) {
        return (
            <div className="d-flex flex-column comment-component page-chat" style={{ alignContent: 'stretch', height: '100%' }}>
                <div className="loading p-10 mt-10 d-flex justify-content-center align-items-center" style={{ height: "100px" }}>
                    <div className="spinner-border" style={{ width: "2rem", height: "2rem", color: "#B5B4A1" }} role="status"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex flex-column comment-component page-chat" style={{ alignContent: 'stretch', height: '100%' }}>
            {/* Header con información del request */}
            <div className="d-flex " style={{ flex: '0 0 auto', background: '#EEE', verticalAlign: 'middle' }}>
                {request && <Card className="mb-1 w-100">
                    <Card.Body className="py-2 px-3">
                        <Row>
                            <Col xs={12}>
                                <div className='d-flex justify-content-between general-text-sm align-items-center'>
                                    <Button
                                        style={{ flex: '0 0 auto' }}
                                        className={`back-button back-button-transparent`}
                                        onClick={() => {
                                            if (window?.history?.length > 1) {
                                                navigate(-1);
                                            } else {
                                                navigate(`/public/`);
                                            }
                                        }}>
                                        <FontAwesomeIcon className="back-button-icon" icon={faArrowLeft} />
                                    </Button>
                                    <div className="d-flex pl-1" style={{ flex: 1, gap: 10, alignItems: 'flex-start' }}>
                                        <div className="mb-0" style={{ display: 'inline-block', verticalAlign: 'text-top', background: '#FFF' }}>{serviceTypeIcon(request.type, 25)}</div>
                                        <h5 className="mb-0 mt-1" style={{ fontSize: 18 }}>{T(`services.type.${request.type}`)}</h5>
                                    </div>
                                    <span style={{ flex: '0 0 auto' }} className={`label label-sm label-light-${request.status} label-inline text-uppercase d-inline-block`}>{T(`services.status.${request.status}`)}</span>
                                </div>
                            </Col>
                            {/* Mostrar información específica según el tipo de request */}
                            {request.type === 'up_selling' && request.data?.photo && (
                                <div className="d-flex mt-4 ml-4">
                                    <img
                                        className="mb-0 mr-2"
                                        src={getCDNImage(request.data.photo)}
                                        style={{ width: 70, height: 70, borderRadius: 8, objectFit: "cover" }}
                                        alt="Row image"
                                    />
                                    <div className="d-flex flex-column flex-grow-1">
                                        {request.data.title && (
                                            <p className="mb-3 mt-2" style={{ fontSize: 14, fontWeight: "bold", lineHeight: 1.2, height: 32, overflow: "hidden", textOverflow: "ellipsis", color: "#777777" }}>
                                                {i18nValue(request.data, 'title', language) || request?.title}
                                            </p>
                                        )}
                                        <div className="d-flex" style={{ textAlign: 'right' }}>
                                            <span className="font-size-sm" style={{ flexGrow: 1 }}>
                                                {format(parseISO(request.createdAt), " dd MMM yyyy", { locale: getLocale(language) })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {request.type === 'general' && (
                                <div className="d-flex justify-content-between align-items-start mt-2 p-3" style={{ width: '100%', height: 'auto' }}>
                                    <div className="d-flex flex-column flex-grow-1">
                                        <div className="text-left" style={{ fontWeight: 'bold', fontSize: 20, color: '#9BB4C4', wordBreak: 'break-word', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: 57 }}>
                                            {T('general.about')}
                                            <span className="ml-2" style={{ fontSize: 20, color: '#777' }}>
                                                {request.observations}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-start mt-2">
                                            <span className="font-size-sm" style={{ fontSize: 12, color: '#777' }}>
                                                {format(parseISO(request.createdAt), "dd MMM yyyy", { locale: getLocale(language) })}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <ChatIcon />
                                    </div>
                                </div>
                            )}
                            {request.type === 'services_booking' && (
                                <div className="d-flex justify-content-between align-items-start mt-2 p-3" style={{ width: '100%', height: 'auto' }}>
                                    <div className="d-flex flex-column flex-grow-1">
                                        <div className="text-left" style={{ fontWeight: 'bold', fontSize: 20, color: '#9BB4C4', wordBreak: 'break-word', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: 57 }}>
                                            {T('general.about')}
                                            <span className="ml-2" style={{ fontSize: 20, color: '#777' }}>
                                                {i18nValue(request.data?.service, "name", language)}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-start mt-2">
                                            <span className="font-size-sm text-success mt-2" style={{ whiteSpace: "nowrap" }}>
                                                {T('cups.booked')}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-start mr-1">
                                            <span className="font-size-sm text-success">
                                                {`${format(parseISO(request.data?.day), "dd MMM yyyy", { locale: getLocale(language) })} - ${request.data?.hour} hs - ${request.data?.qty} ${T("cups.spots")}`}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <ChatIcon />
                                    </div>
                                </div>
                            )}
                        </Row>
                    </Card.Body>
                </Card>}
            </div>

            {/* Container con los comentarios */}
            <Container className="pb-2" style={{ width: '90%', flex: '1 1 auto', overflowY: 'auto' }} ref={refItemsContainer}>
                {items && items.length === 0 && <div
                    className="loading p-10 mt-10 d-flex justify-content-center align-items-center"
                    style={{ height: "100px" }}
                >
                    <div className="spinner-border" style={{ width: "2rem", height: "2rem", color: "#B5B4A1" }} role="status"></div>
                </div>}
                {request?.type === 'general' && items && items.length !== 0 && !items[1]?.comment && <div className="d-flex flex-column align-items-center justify-content-center mt-4 text-center" style={{ height: '50%' }}>
                    <div>
                        <Empty width={80} height={80} />
                        <p className="mt-4" style={{ fontSize: '1.2rem', color: '#777' }}>
                            {T('status.no_messages')}
                        </p>
                    </div>
                </div>}
                {items && items.map((item, idx) => {
                    if (!item.comment) return false;
                    const _join_comment = prevMessageUser === item.userId;
                    prevMessageUser = item.userId;
                    const _createdDate = Date.parse(item.createdAt);
                    const _ago = formatDistanceToNow(_createdDate, { addSuffix: true, locale: language === 'es' ? es : language === 'de' ? de : enUS });

                    const isSender = item.userId;
                    const alignment = isSender ? 'flex-start' : 'flex-end';
                    const marginLeft = isSender ? '0' : 'auto';
                    const marginRight = isSender ? 'auto' : '0';
                    const isLastMessageByUser = lastMessageIndexByUser[item.userId] === idx;

                    return (
                        <div
                            key={`comment-item-${item.id}`}
                            className={`comment-item ${item.userId ? 'from-user' : ''}`}
                            style={{ marginTop: !_join_comment ? '1rem' : '0.5rem' }}
                        >
                            <Row>
                                <Col
                                    xs={12}
                                    className="d-flex flex-row"
                                    style={{
                                        justifyContent: alignment,
                                        gap: '3px',
                                        flexShrink: 1,
                                        alignItems: 'end',
                                        marginLeft,
                                        marginRight
                                    }}
                                >
                                    {item.userId && (
                                        <div
                                            className="d-flex flex-column mr-2"
                                            style={{
                                                flexBasis: '30px',
                                                flexShrink: 0,
                                                backgroundImage: hotel?.logo ? `url(${getCDNImage(hotel.logo)})` : 'none',
                                                backgroundPosition: 'center',
                                                backgroundSize: 'cover',
                                                backgroundRepeat: 'no-repeat',
                                                backgroundColor: '#DDD',
                                                height: '30px',
                                                width: '30px',
                                                borderRadius: '15px',
                                                borderColor: 'black',
                                                borderStyle: 'solid',
                                                borderWidth: '1px',
                                                padding: '2px',
                                                visibility: !isLastMessageByUser ? 'hidden' : 'visible',
                                            }}
                                        ></div>
                                    )}

                                    <div className="d-flex comment-content">
                                        <p
                                            className="comment-message mb-0 general-text"
                                            style={{ whiteSpace: 'pre-wrap', textAlign: isSender ? 'left' : 'right' }}
                                            dangerouslySetInnerHTML={{ __html: item.comment }}
                                        ></p>
                                        <span className="comment-time general-text-sm" style={{ textAlign: isSender ? 'left' : 'right' }}>{_ago}</span>
                                    </div>

                                    {!item.userId && (
                                        <div
                                            className="d-flex flex-column justify-content-center align-items-center"
                                            style={{
                                                flexBasis: '30px',
                                                flexShrink: 0,
                                                height: '30px',
                                                width: '30px',
                                                borderRadius: '50%',
                                                backgroundColor: '#DDD',
                                                visibility: !_join_comment && !isLastMessageByUser ? 'hidden' : 'visible',
                                                borderColor: 'black',
                                                borderStyle: 'solid',
                                                borderWidth: '1px',
                                            }}
                                        >
                                            <Avatar width={20} height={20} />
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    );
                })}
            </Container>

            {/* Footer con input para comentarios */}
            <div className="p-2 d-flex" style={{ flex: '0 0 auto', background: '#FFF', borderTop: '1px solid #DDD', gap: 7, alignItems: "end" }}>
                <Form.Control
                    className="chat-textarea general-text-sm"
                    type={'textarea'}
                    value={undefined}
                    placeholder={T("general.type_message")}
                    ref={refComment}
                    maxLength={200}
                    onChange={handleInputHeight}
                    as="textarea"
                    rows={1}
                    style={{ border: "none", fontSize: "0.75rem", resize: "none" }}
                />
                {!sending ? <Button
                    id="btnSend"
                    style={{ background: '#6993FF', border: "none", flex: '0 0 36px' }}
                    size={'sm'}
                    variant={'secondary'}
                    type={'button'}
                    block={false}
                    disabled={!message || sending}
                    onClick={() => _addComment(message)}>
                    <FontAwesomeIcon size="sm" icon={faPaperPlane} color="#FFF" />
                </Button>
                    :
                    <div className="loading" style={{ flex: '0 0 36px' }}><div className="spinner-border" role="status"></div></div>
                }
            </div>
        </div >)
};

export default PublicChat;

